var I18nState_1;
import { __decorate, __metadata } from "tslib";
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, DOCUMENT } from '@angular/core';
import { ConfigService } from '@core/shared/config';
import { GetI18nDataRequest, GetI18nLanguagesRequest } from '@core/shared/i18n';
import { TranslateService } from '@ngx-translate/core';
import { Action, Actions, Selector, State, Store, createSelector, ofActionCompleted } from '@ngxs/store';
import * as deepmerge from 'deepmerge';
import { share, take, tap } from 'rxjs';
import { AppRequestService } from '../api';
import { CORE_CONFIG_FRONTEND } from '../constants';
import { PersistStateFactory } from '../utils';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
import * as i2 from "../api";
import * as i3 from "@ngx-translate/core";
import * as i4 from "@core/shared/config";
import * as i5 from "../utils";
const STORAGE_KEY_LANGUAGE_SELECTION = 'user-language-selection';
export var I18nActions;
(function (I18nActions) {
  let PatchLanguageData = /*#__PURE__*/(() => {
    class PatchLanguageData {
      static {
        this.type = '[I18n] SetLanguageData';
      }
      constructor(requestedLanguage, resolvedLanguage, data) {
        this.requestedLanguage = requestedLanguage;
        this.resolvedLanguage = resolvedLanguage;
        this.data = data;
      }
    }
    return PatchLanguageData;
  })();
  I18nActions.PatchLanguageData = PatchLanguageData;
  let SetKnownLanguages = /*#__PURE__*/(() => {
    class SetKnownLanguages {
      static {
        this.type = '[I18n] SetKnownLanguages';
      }
      constructor(list) {
        this.list = list;
      }
    }
    return SetKnownLanguages;
  })();
  I18nActions.SetKnownLanguages = SetKnownLanguages;
  // explicitly sets preferred language (or unsets explicit choice, if null)
  let SelectLanguage = /*#__PURE__*/(() => {
    class SelectLanguage {
      static {
        this.type = '[I18n] SelectLanguage';
      }
      constructor(lang = null) {
        this.lang = lang;
      }
    }
    return SelectLanguage;
  })();
  I18nActions.SelectLanguage = SelectLanguage;
  // (loads and) gets language data 
  let LoadLanguageData = /*#__PURE__*/(() => {
    class LoadLanguageData {
      static {
        this.type = '[I18n] LoadLanguageData';
      }
      constructor(refresh = false, lang = null) {
        this.refresh = refresh;
        this.lang = lang;
      }
    }
    return LoadLanguageData;
  })();
  I18nActions.LoadLanguageData = LoadLanguageData;
})(I18nActions || (I18nActions = {}));
let I18nState = /*#__PURE__*/(() => {
  let I18nState = class I18nState {
    static {
      I18nState_1 = this;
    }
    static selectedLanguageData(languageId, state) {
      const staticData = state.staticData[languageId];
      const lazyData = state.data[languageId];
      if (typeof staticData === 'undefined' && typeof lazyData === 'undefined') return null;
      return deepmerge(staticData || {}, lazyData || {});
    }
    static getLanguageData(languageId) {
      return createSelector([I18nState_1], state => {
        const staticData = state.staticData[languageId];
        const lazyData = state.data[languageId];
        if (typeof staticData === 'undefined' && typeof lazyData === 'undefined') return null;
        return deepmerge(staticData || {}, lazyData || {});
      });
    }
    static calculatedLanguage(state) {
      const lang = state.forcedLanguage || state.selectedLanguage || state.browserLanguage;
      const knownLangs = state.knownLanguages || [];
      if (lang) {
        // language may use long or short iso format e.g. de, en-US.
        if (knownLangs.includes(lang)) return lang;
        const isLongLangId = lang.includes('-');
        if (isLongLangId) {
          const basicLanguageId = lang.split('-')[0];
          if (knownLangs.includes(basicLanguageId)) return basicLanguageId;
        }
      }
      // could not find a fitting language, fallback.
      return knownLangs?.[0] || null;
    }
    static isAnyLanguageDataAvailable(state) {
      return Object.keys(state.data).length > 0 || Object.keys(state.staticData).length > 0;
    }
    static isAnyLazyLanguageDataLoaded(state) {
      return Object.keys(state.data).length > 0;
    }
    constructor(actions$, api, store, i18n, config, persistenceFactory, document, coreConfig, platformId) {
      this.actions$ = actions$;
      this.api = api;
      this.store = store;
      this.i18n = i18n;
      this.config = config;
      this.persistenceFactory = persistenceFactory;
      this.document = document;
      this.coreConfig = coreConfig;
      this.platformId = platformId;
      this.persistedLanguageSelection = this.persistenceFactory.create(STORAGE_KEY_LANGUAGE_SELECTION, false, 'local');
    }
    ngxsOnInit(ctx) {
      try {
        // try to set up basic static i18n data from preloaded config
        const staticI18nString = this.config.get('staticI18n', '{}');
        const staticI18n = JSON.parse(staticI18nString);
        const staticLanguageIds = Object.keys(staticI18n);
        ctx.patchState({
          staticData: staticI18n,
          knownLanguages: staticLanguageIds
        });
      } catch (err) {
        console.error(err);
      }
      if (isPlatformBrowser(this.platformId)) {
        const browserLanguage = window.navigator.language;
        // TODO: window.navigator.languages can be used to get an array of all accepted languages.
        ctx.patchState({
          browserLanguage
        });
      }
      const forcedLanguage = this.config.get('i18n.forcedLanguage', null);
      if (forcedLanguage) ctx.patchState({
        forcedLanguage
      });
      const userSelection = this.persistedLanguageSelection.get();
      ctx.dispatch(new I18nActions.SelectLanguage(userSelection || null));
      if (!this.coreConfig.serverless) {
        // configurate at CoreFrontendSharedModule.forRoot
        this.api.sendRequest(GetI18nLanguagesRequest).subscribe(response => {
          this.store.dispatch(new I18nActions.SetKnownLanguages(response.languageIds));
        });
      }
      this.createSubscriptions(ctx);
    }
    createSubscriptions(ctx) {
      // update document language attribute accordingly
      this.store.select(I18nState_1.calculatedLanguage).subscribe(calculatedLanguage => {
        this.document.documentElement.setAttribute('lang', calculatedLanguage);
        // console.log('i18n > calculatedLanguage changed, load language data')
        ctx.dispatch(new I18nActions.LoadLanguageData(false));
        this.actions$.pipe(ofActionCompleted(I18nActions.PatchLanguageData), take(1)).subscribe(() => {
          // once language data has been loaded, patch it ti i18n service.
          // service will ask loader, which then requests the new language data from I18nState.
          // console.log('i18n > load language data completed, reload i18nService')
          this.i18n.use(calculatedLanguage);
        });
      });
    }
    /**
     * action has info on both requested and resolved language.
     * In theory they could differ... frontend will ignore if it is different and just treat the resolved data
     * as if it was exactly for requested language.
     * TODO: should language info be set to the resolved language ID? Or does it not matter?
     */
    setLanguageData(ctx, action) {
      const {
        data,
        requestedLanguage,
        resolvedLanguage
      } = action;
      const existingData = ctx.getState().data;
      // always patching instead of overwriting allows us to call SetLanguageData with empty data in case no refresh is needed.
      // this simplifies reacting to incoming language data, cause setLanguageData can be called even if no reload was executed.
      const patchedLanguageData = {
        ...(existingData[requestedLanguage] || {}),
        ...data
      };
      ctx.patchState({
        data: {
          ...ctx.getState().data,
          [requestedLanguage]: patchedLanguageData
        }
      });
    }
    setKnownLanguages(ctx, action) {
      ctx.patchState({
        knownLanguages: action.list
      });
    }
    selectLanguage(ctx, action) {
      ctx.patchState({
        selectedLanguage: action.lang
      });
    }
    // lazy-load data of a language from server, optionally with cache or forced reload
    loadLanguageData(ctx, action) {
      const state = ctx.getState();
      const language = I18nState_1.calculatedLanguage(state);
      if (!action.refresh && state.data[language] || this.coreConfig.serverless) {
        // important to trigger action listeners!
        this.store.dispatch(new I18nActions.PatchLanguageData(language, language, {}));
        return;
      } else {
        return this.api.sendRequest(GetI18nDataRequest, {
          language
        }).pipe(share(), tap(response => {
          this.store.dispatch(new I18nActions.PatchLanguageData(language, response.languageId, response.data));
        }));
      }
    }
    static {
      this.ɵfac = function I18nState_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || I18nState)(i0.ɵɵinject(i1.Actions), i0.ɵɵinject(i2.AppRequestService), i0.ɵɵinject(i1.Store), i0.ɵɵinject(i3.TranslateService), i0.ɵɵinject(i4.ConfigService), i0.ɵɵinject(i5.PersistStateFactory), i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(CORE_CONFIG_FRONTEND), i0.ɵɵinject(PLATFORM_ID));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: I18nState,
        factory: I18nState.ɵfac
      });
    }
  };
  __decorate([Action(I18nActions.PatchLanguageData), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, I18nActions.PatchLanguageData]), __metadata("design:returntype", void 0)], I18nState.prototype, "setLanguageData", null);
  __decorate([Action(I18nActions.SetKnownLanguages), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, I18nActions.SetKnownLanguages]), __metadata("design:returntype", void 0)], I18nState.prototype, "setKnownLanguages", null);
  __decorate([Action(I18nActions.SelectLanguage), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, I18nActions.SelectLanguage]), __metadata("design:returntype", void 0)], I18nState.prototype, "selectLanguage", null);
  __decorate([Action(I18nActions.LoadLanguageData), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, I18nActions.LoadLanguageData]), __metadata("design:returntype", void 0)], I18nState.prototype, "loadLanguageData", null);
  __decorate([Selector([I18nState.calculatedLanguage, I18nState]), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, Object]), __metadata("design:returntype", void 0)], I18nState, "selectedLanguageData", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [String]), __metadata("design:returntype", void 0)], I18nState, "getLanguageData", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", String)], I18nState, "calculatedLanguage", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Boolean)], I18nState, "isAnyLanguageDataAvailable", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Boolean)], I18nState, "isAnyLazyLanguageDataLoaded", null);
  return I18nState;
})();
I18nState = I18nState_1 = __decorate([State({
  name: 'i18n',
  defaults: {
    browserLanguage: null,
    selectedLanguage: null,
    knownLanguages: null,
    staticData: {},
    data: {}
  }
}), __metadata("design:paramtypes", [Actions, AppRequestService, Store, TranslateService, ConfigService, PersistStateFactory, Document, Object, Object])], I18nState);
export { I18nState };